import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { listPosts } from '../services/api';

// Ordered shortest -> longest so the dropdown reads sensibly.
const WITHIN_OPTIONS = [
  { value: '1d', label: '1 Day', seconds: 24 * 60 * 60 },
  { value: '1m', label: '1 Month', seconds: 30 * 24 * 60 * 60 },
  { value: '6m', label: '6 Months', seconds: 182 * 24 * 60 * 60 },
  { value: '1y', label: '1 Year', seconds: 365 * 24 * 60 * 60 }
];

function Home() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [within, setWithin] = useState('1m');

  useEffect(() => {
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const data = await listPosts(filter ? { animal_type: filter } : {});
      setPosts(data.posts || []);
    } catch (err) {
      console.error('Failed to load posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const animalTypes = ['horse', 'sheep', 'goat', 'chicken', 'cattle', 'pig'];

  // Client-side search + "posted within" filtering.
  const nowSec = Math.floor(Date.now() / 1000);
  const windowSec = WITHIN_OPTIONS.find((o) => o.value === within)?.seconds ?? Infinity;
  const q = search.trim().toLowerCase();
  const visiblePosts = posts.filter((p) => {
    if (p.created_at && nowSec - p.created_at > windowSec) return false;
    if (!q) return true;
    return [p.title, p.details, p.location, p.animal_type]
      .filter(Boolean)
      .some((f) => f.toLowerCase().includes(q));
  });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Browse Listings</h1>
        <Link className="btn btn-primary" to="/dashboard">
          + New Listing
        </Link>
      </div>

      <div className="row g-2 mb-4 align-items-end">
        <div className="col-md-3">
          <label className="form-label">Animal Type</label>
          <select
            className="form-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="">All</option>
            {animalTypes.map((type) => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6">
          <label className="form-label">Search</label>
          <input
            type="text"
            className="form-control"
            placeholder="Search title, details, location…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">Posted within</label>
          <select
            className="form-select"
            value={within}
            onChange={(e) => setWithin(e.target.value)}
          >
            {WITHIN_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : visiblePosts.length === 0 ? (
        <div className="alert alert-info">No listings match your filters.</div>
      ) : (
        <div>
          {visiblePosts.map((post) => (
            <div key={post.post_id} className="card mb-2">
              <div className="row g-0 align-items-center flex-nowrap">
                {post.images && post.images.length > 0 && (
                  <div className="col-auto">
                    <img
                      src={post.images[0]}
                      alt={post.title}
                      className="hl-list-thumb"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="col">
                  <div className="card-body py-2">
                    <h6 className="card-title mb-1">{post.title}</h6>
                    <div className="text-muted small">
                      {post.animal_type.toUpperCase()} • {post.location || 'Unknown'}
                    </div>
                    <strong className="text-success">${post.price || '0'}</strong>
                  </div>
                </div>
                <div className="col-auto pe-3">
                  <Link
                    to={`/posts/${post.post_id}`}
                    className="btn btn-sm btn-outline-primary hl-more"
                  >
                    more info…
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Home;
